import { css as css$1, useTheme, jsx, Global, CacheProvider, ThemeProvider as ThemeProvider$1 } from '@emotion/react';
export { CacheProvider, ClassNames, Global, ThemeContext, createElement, jsx, keyframes, useTheme, withEmotionCache, withTheme } from '@emotion/react';
import React from 'react';
import createCache from '@emotion/cache';
import packageInfo from '@emotion/react/package.json';
import { writeVars } from '@delight-js/utils';

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }

  return arr2;
}

function _arrayWithoutHoles(arr) {
  if (Array.isArray(arr)) return _arrayLikeToArray(arr);
}

function _iterableToArray(iter) {
  if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _nonIterableSpread() {
  throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _toConsumableArray(arr) {
  return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray(arr) || _nonIterableSpread();
}

var getClassFromTheme = function getClassFromTheme(theme) {
  return function (match, themeLink) {
    if (theme !== null && theme !== void 0 && theme.map && theme.map[themeLink]) {
      // parse the exchanged code one more time => allows the use of themeLinks in the themeLink definitions
      return parseCss(theme.map[themeLink], theme);
    }

    console.warn("Theme link \"".concat(themeLink, "\" not found."));
    return themeLink;
  };
};

var joinTemplate = function joinTemplate(strings, expressions) {
  return strings.reduce(function (result, currentString, i) {
    var _expressions$i;

    return "".concat(result).concat(currentString).concat((_expressions$i = expressions[i]) !== null && _expressions$i !== void 0 ? _expressions$i : "");
  });
};

var isNumber = function isNumber(v) {
  return !Number.isNaN(v);
};

var nextNumber = function nextNumber(currentIndex) {
  return function (v, index) {
    return index > currentIndex && isNumber(v);
  };
};

var createLinearFunction = function createLinearFunction(prev, next) {
  return function (breakpoint) {
    var m = (next.v - prev.v) / (next.bp - prev.bp);
    var b = prev.v - m * prev.bp;
    return m * breakpoint + b;
  };
};

var interpolateMediaqueries = function interpolateMediaqueries(theme) {
  return function (match, g1, property, g3, stiffValues, g5, smoothValues, g7, g8, options, g10, unit) {
    var _theme$definitions;

    if (!(theme !== null && theme !== void 0 && (_theme$definitions = theme.definitions) !== null && _theme$definitions !== void 0 && _theme$definitions.breakpoint)) {
      console.warn("Breakpoints not defined.");
      return match;
    }

    var breakpoints = Object.entries(theme.definitions.breakpoint);

    if (!(stiffValues || smoothValues)) {
      console.warn("Values for mediaqueries not set.");
      return match;
    }

    var shouldInterpolate = !!smoothValues;
    var values = (smoothValues || stiffValues).split(",").map(function (v) {
      return parseFloat(v.trim());
    });

    if (options) {
      try {
        options = JSON.parse(options.trim());
      } catch (e) {
        console.log(e);
      }

      if (!options.method || !["floor", "round", "ceil"].includes(options.method)) {
        options.method = "round";
      }

      options.step = parseFloat(options.step);

      if (Number.isNaN(options.step) || options.step <= 0) {
        options.step = unit === "em" ? 0.001 : 1;
      }
    } // TODO: line-height: {,40,,,,104}px; interpolate first value and start with mediaqueries from third value


    var output = [];

    for (var i = 0, len = values.length; i < len; i++) {
      if (Number.isNaN(values[i]) && !shouldInterpolate) {
        continue;
      }

      var before = "",
          after = "",
          finalValue = void 0;
      var breakpoint = breakpoints[i];

      if (breakpoint[0] !== "default") {
        before = "@media:".concat(breakpoint[0], " {");
        after = "}";
      }

      if (!Number.isNaN(values[i])) {
        finalValue = values[i];
      } else if (shouldInterpolate) {
        // TODO: fluent interpolation like calc(4px + 0.2vw) when option.step < 0
        // calculate value between other values
        // get last
        // get next
        var nextIndex = values.findIndex(nextNumber(i));

        var prevIndex = len - 1 - _toConsumableArray(values).reverse().findIndex(nextNumber(i));

        if (nextIndex < 0 || prevIndex < 0) {
          // TODO: can also interpolate edge cases from the next and second next value...
          continue;
        } // create formula


        var func = createLinearFunction({
          v: values[prevIndex],
          bp: breakpoints[prevIndex][1]
        }, {
          v: values[nextIndex],
          bp: breakpoints[nextIndex][1]
        });
        finalValue = func(breakpoints[i][1]);

        if (options) {
          finalValue = Math[options.method](finalValue / options.step) * options.step;
        }
      }

      output.push("".concat(before).concat(property, ": ").concat(finalValue).concat(unit, ";").concat(after));
    }

    return output.join("\n");
  };
};

var expandMediaquery = function expandMediaquery(theme) {
  return function (match, breakpoint) {
    var _theme$definitions2;

    if (theme !== null && theme !== void 0 && (_theme$definitions2 = theme.definitions) !== null && _theme$definitions2 !== void 0 && _theme$definitions2.breakpoint && theme.definitions.breakpoint[breakpoint]) {
      return "@media screen and (min-width: ".concat(theme.definitions.breakpoint[breakpoint], "px)");
    }

    console.warn("Breakpoint \"".concat(breakpoint, "\" not defined."));
    return breakpoint;
  };
};

var parseActions = [{
  name: "Map variables to their values",
  regExp: /\$([A-Za-z0-9._-]+)/gm,
  func: getClassFromTheme
}, {
  name: "Map variables  a second time to parse ",
  regExp: /\$([A-Za-z0-9._-]+)/gm,
  func: getClassFromTheme
}, {
  name: "Fill mediaqueries and interpolate",
  // regExp: /(^|\\s+|\}|;)([A-Za-z0-9-]+):\s*(\[(.*)\]|\{(.*)\})(\((.*)\))?([A-Za-z]*);/gm,
  regExp: /(^|\s+|\}|;)([A-Za-z0-9-]+):\s*(\[(((?!\]).)*)\]|\{(((?!\}).)*)\})(\((((?!\)).)*)\))?([A-Za-z]*);/gm,
  func: interpolateMediaqueries
}, {
  name: "Turn media:<breakpoint> short notes into real mediaqueries.",
  regExp: /@media:([A-Za-z0-9._-]+)/gm,
  func: expandMediaquery
}];
/*
 * Replace all theme links
 * @param {string} style - The preprocessed css string with theme links included.
 */

var parseCss = function parseCss(style, theme) {
  // TODO: style may be an object
  if (typeof style !== "string") {
    return style;
  }

  var newStyle = style;

  for (var i = 0, len = parseActions.length; i < len; i++) {
    var action = parseActions[i];
    newStyle = newStyle.replace(action.regExp, action.func(theme));
  }

  return newStyle;
};

var css = function css(arg1) {
  for (var _len = arguments.length, otherArgs = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
    otherArgs[_key - 1] = arguments[_key];
  }

  return function (theme) {
    // test if it is template tag
    var isTag = !!(Array.isArray(arg1) && arg1.length && !!arg1.raw && Array.isArray(arg1.raw) && arg1.raw.length === arg1.length && // Object.isFrozen(arg1) &&
    otherArgs.length + 1 === arg1.length);
    var args = isTag ? [joinTemplate(arg1, otherArgs)] : [arg1].concat(otherArgs);
    var newArgs = args.map(function (a) {
      return parseCss(a, theme);
    });
    return css$1.apply(void 0, _toConsumableArray(newArgs));
  };
};

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};

  var target = _objectWithoutPropertiesLoose(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);

    if (enumerableOnly) {
      symbols = symbols.filter(function (sym) {
        return Object.getOwnPropertyDescriptor(object, sym).enumerable;
      });
    }

    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  var _i = arr && (typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"]);

  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;

  var _s, _e;

  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}

function _typeof(obj) {
  "@babel/helpers - typeof";

  if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") {
    _typeof = function _typeof(obj) {
      return typeof obj;
    };
  } else {
    _typeof = function _typeof(obj) {
      return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj;
    };
  }

  return _typeof(obj);
}

var CssLayer = function CssLayer(_ref) {
  var type = _ref.type;
  var theme = useTheme(); // TODO: concat default vars

  var _theme$render$type = theme.render[type],
      _theme$render$type$va = _theme$render$type.vars,
      vars = _theme$render$type$va === void 0 ? [] : _theme$render$type$va,
      _theme$render$type$st = _theme$render$type.styles,
      styles = _theme$render$type$st === void 0 ? [] : _theme$render$type$st;
  return jsx(React.Fragment, null, jsx(Global, {
    styles: css("\n          ".concat(vars.map(function (v) {
      return Object.entries(v.vars).map(function (w) {
        return writeVars.apply(void 0, _toConsumableArray(w));
      }).join("");
    }).join(""), "\n          ").concat(Object.entries(styles).map(function (_ref2) {
      var _ref3 = _slicedToArray(_ref2, 2),
          tag = _ref3[0],
          tagStyles = _ref3[1];

      return "".concat(tag, " { ").concat(tagStyles, " }");
    }).join(" "), "\n        "))(theme)
  }));
};

var CssBase$1 = function CssBase() {
  return /*#__PURE__*/React.createElement(CssLayer, {
    type: "base"
  });
};

var myCache = createCache({
  key: "util" // css-utils

});

var CssBase = function CssBase() {
  return /*#__PURE__*/React.createElement(CacheProvider, {
    value: myCache
  }, /*#__PURE__*/React.createElement(CssLayer, {
    type: "utils"
  }));
};

var _excluded = ["children", "theme", "isSsr", "cache"];

var isObject = function isObject(obj) {
  return _typeof(obj) === "object" && obj !== null;
};

var walkObj = function walkObj(obj) {
  var path = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
  var onEnd = arguments.length > 2 ? arguments[2] : undefined;

  if (isObject(obj)) {
    Object.entries(obj).forEach(function (_ref) {
      var _ref2 = _slicedToArray(_ref, 2),
          key = _ref2[0],
          obj2 = _ref2[1];

      return walkObj(obj2, [].concat(_toConsumableArray(path), [key]), onEnd);
    });
  } else {
    onEnd({
      path: path,
      value: obj
    });
  }
};

var createTheme = function createTheme(theme) {
  /*
   * 1. Read each domains config
   * 2. Process every domain
   * 3. Merge in theme
   *
   */
  var map = {};
  walkObj(theme.definitions, [], function (_ref3) {
    var path = _ref3.path,
        value = _ref3.value;
    map[path.join("-")] = value;
  });
  return {
    type: "theme",
    definitions: theme.definitions,
    render: {
      base: theme.base || {},
      ui: theme.ui || {},
      utils: theme.utils || {}
    },
    map: _objectSpread2(_objectSpread2({}, map), theme.ui)
  };
};

var __EMOTION_VERSION__ = packageInfo.version;
var Context = /*#__PURE__*/React.createContext({
  __EMOTION_VERSION__: __EMOTION_VERSION__,
  theme: {}
});

var useUmbrellaTheme = function useUmbrellaTheme() {
  return React.useContext(Context);
};

var createContentCache = function createContentCache() {
  return createCache({
    key: "c"
  });
};

var contentCache = createContentCache();

var ThemeProvider = function ThemeProvider(_ref4) {
  var children = _ref4.children,
      theme = _ref4.theme,
      _ref4$isSsr = _ref4.isSsr,
      isSsr = _ref4$isSsr === void 0 ? true : _ref4$isSsr,
      cache = _ref4.cache,
      props = _objectWithoutProperties(_ref4, _excluded);

  var outer = useUmbrellaTheme();

  if (process.env.NODE_ENV !== "production") {
    if (outer.__EMOTION_VERSION__ !== __EMOTION_VERSION__) {
      console.warn("Multiple versions of Emotion detected,", "and theming might not work as expected.", "Please ensure there is only one copy of @emotion/react installed in your application.");
    }
  }

  var cacheValue = cache || isSsr ? createContentCache() : contentCache; // TODO: process theme with theme factory.

  return /*#__PURE__*/React.createElement(ThemeProvider$1, _extends({
    theme: createTheme(theme)
  }, props), /*#__PURE__*/React.createElement(CssBase$1, null), /*#__PURE__*/React.createElement(CacheProvider, {
    value: cacheValue
  }, children), /*#__PURE__*/React.createElement(CssBase, null));
};

export { ThemeProvider, css };